import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { navItems, siteInfo } from "@/mocks/site";
import SearchBox from "@/components/feature/SearchBox";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setMobileOpen(false);
    setCatOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === "/") return location.pathname === "/";
    return location.pathname.startsWith(path.split("/").slice(0, 2).join("/"));
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="hidden bg-primary-950 text-background-100 md:block">
        <div className="flex items-center justify-between gap-4 px-6 py-2 text-xs">
          <span className="flex items-center gap-2">
            <i className="ri-megaphone-line text-primary-300"></i>
            每日更新热榜话题时间线，整理公开信息，不做结论
          </span>
          <span className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <i className="ri-mail-line text-primary-300"></i>
              {siteInfo.email}
            </span>
            <Link to="/contact" className="text-primary-200 transition-colors hover:text-background-50">
              纠错与投稿 <i className="ri-arrow-right-s-line"></i>
            </Link>
          </span>
        </div>
      </div>

      <div className="border-b border-background-200 bg-background-50/95 backdrop-blur">
        <div className="flex h-16 items-center gap-4 px-4 md:px-6">
          <Link to="/" className="flex shrink-0 items-center gap-2">
            <span className="flex h-9 w-9 items-center justify-center rounded-md bg-accent-500 text-background-50">
              <i className="ri-book-2-line text-lg"></i>
            </span>
            <span className="leading-tight">
              <span className="block font-heading text-lg font-bold tracking-wide text-foreground-950">
                {siteInfo.name}
              </span>
              <span className="hidden text-[11px] text-foreground-500 sm:block">人物词条 · 话题档案库</span>
            </span>
          </Link>

          <nav aria-label="主导航" className="ml-4 hidden items-center gap-1 md:flex">
            {navItems.map((item) =>
              item.label === "词条分类" ? (
                <div key={item.label} className="relative">
                  <button
                    type="button"
                    onClick={() => setCatOpen((prev) => !prev)}
                    aria-expanded={catOpen}
                    className={`flex cursor-pointer items-center gap-1 whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                      catOpen || isActive("/category")
                        ? "bg-primary-100 text-primary-800"
                        : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                    }`}
                  >
                    {item.label}
                    <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? "rotate-180" : ""}`}></i>
                  </button>
                  {catOpen && (
                    <div className="absolute left-0 top-full z-50 mt-2 w-64 overflow-hidden rounded-md border border-background-200 bg-background-50 p-2">
                      {categories.map((cat) => (
                        <Link
                          key={cat.id}
                          to={`/category/${cat.id}`}
                          className="flex items-start gap-3 rounded-md px-3 py-2 transition-colors hover:bg-primary-50"
                        >
                          <i className={`${cat.icon} mt-0.5 text-base text-accent-600`}></i>
                          <span>
                            <span className="block text-sm text-foreground-900">{cat.name}</span>
                            <span className="block text-xs text-foreground-500">{cat.entryCount} 个词条</span>
                          </span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.label}
                  to={item.path}
                  className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                    isActive(item.path)
                      ? "bg-primary-100 text-primary-800"
                      : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
                  }`}
                >
                  {item.label}
                </Link>
              ),
            )}
          </nav>

          <div className="ml-auto hidden w-64 lg:block">
            <SearchBox />
          </div>

          <Link
            to="/ranking"
            className="ml-auto hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm text-background-50 transition-colors hover:bg-primary-600 lg:ml-2 lg:inline-flex"
          >
            看热榜
          </Link>

          <button
            type="button"
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            onClick={() => setMobileOpen((prev) => !prev)}
            className="ml-auto flex h-10 w-10 cursor-pointer items-center justify-center rounded-md border border-background-300 text-foreground-800 md:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-xl`}></i>
          </button>
        </div>

        {mobileOpen && (
          <div className="border-t border-background-200 bg-background-50 px-4 pb-5 pt-4 md:hidden">
            <SearchBox />
            <nav aria-label="移动端导航" className="mt-4 flex flex-col gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.label}
                  to={item.path}
                  className="rounded-md px-3 py-2.5 text-sm text-foreground-800 transition-colors hover:bg-primary-50"
                >
                  {item.label}
                </Link>
              ))}
            </nav>
            <p className="mt-4 mb-2 px-3 text-xs uppercase tracking-wider text-foreground-400">词条分类</p>
            <div className="grid grid-cols-2 gap-2">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/category/${cat.id}`}
                  className="flex items-center gap-2 rounded-md border border-background-200 px-3 py-2 text-xs text-foreground-700 transition-colors hover:border-primary-300"
                >
                  <i className={`${cat.icon} text-accent-600`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </header>
  );
}